/**
 * serializeDest() (T-12, DS-1g-04 §4.7) — AC-1g-14.
 *
 * ONE locus mapping the SaveScreen destination-flag UI state to the wire
 * `_meta` shape (the E-1i-DEST-FILTER contract, 1g = WRITER; the contract
 * itself is textual — DS-1g-04 §4.7 / receipts.md — not a published JSON
 * schema; 1h-lite consumes and validates on its own side):
 *
 *   - untouched          -> `_meta.report_dest` ABSENT (key not present)
 *   - internal-only      -> `_meta.report_dest: "internal_only"`
 *   - daily/weekly combo -> `_meta.report_dest: [subset of "daily"|"weekly"]`
 *   - client flag        -> `_meta.client_intent: true` ONLY
 *
 * `DestUIState`'s input type makes malformed output inexpressible: `choice`
 * is a discriminated union (untouched | internal_only | reports), so there
 * is no way to construct a state that is simultaneously internal-only AND
 * carries daily/weekly values. Internal-only is mutually exclusive with
 * `client_intent` too — this function drops `client` when `choice.kind ===
 * "internal_only"`, mirroring the UI-level exclusivity (§4.7) as a second,
 * belt-and-suspenders enforcement at the serialization boundary.
 *
 * This module has NO code path that writes `_meta.visibility` anywhere —
 * grep guard TW-1i-RELEASE-SETTER (see serialize-dest.test.ts).
 */

export type DestChoice =
  | { kind: "untouched" }
  | { kind: "internal_only" }
  | { kind: "reports"; daily: boolean; weekly: boolean };

/** The full UI state this serializer accepts. `client` is the *Client
 * report* flag's structural-gate-confirmed value (T-13 owns the second
 * confirm that sets it — this module only serializes whatever it is
 * handed). */
export interface DestUIState {
  choice: DestChoice;
  client: boolean;
}

export interface SerializedDest {
  report_dest?: "internal_only" | Array<"daily" | "weekly">;
  client_intent?: true;
}

/**
 * Which emission this is. The ONLY difference is what "reports, none ticked"
 * means, and it genuinely differs (Red, 2026-07-31: "once something is added to
 * a report there is no way to remove it without deleting the whole entry"):
 *
 *   - `create`: nothing is on a report yet, so ticking a box and un-ticking it
 *     leaves the capture exactly where it started. Absent, per AC-1g-14.
 *   - `edit`: the entry may ALREADY be on a report, and absent means UNCHANGED
 *     all the way down the INV-4 chain — so an absent key would re-affirm the
 *     membership the human just cleared. Worse than a no-op: absent resolves to
 *     `Dest::Legacy` server-side, which admits BOTH daily and weekly, so
 *     un-ticking Daily would have quietly ADDED the entry to the weekly.
 *
 * `[]` is not a new contract — `visibility.rs` already defines an empty list as
 * "admits neither ... reached via an explicit empty array rather than the tag"
 * and tests it. The client simply had no way to say it.
 *
 * `untouched` stays absent in BOTH modes: it is the state of a human who never
 * touched the control, and inferring an intent from that is exactly the guessing
 * rule #2 forbids.
 */
export type DestMode = "create" | "edit";

export function serializeDest(state: DestUIState, mode: DestMode = "create"): SerializedDest {
  const result: SerializedDest = {};

  if (state.choice.kind === "internal_only") {
    result.report_dest = "internal_only";
  } else if (state.choice.kind === "reports") {
    const values: Array<"daily" | "weekly"> = [];
    if (state.choice.daily) values.push("daily");
    if (state.choice.weekly) values.push("weekly");
    if (values.length > 0) {
      result.report_dest = values;
    } else if (mode === "edit") {
      // The human cleared every box on an entry that may be on a report. Say so.
      result.report_dest = [];
    }
  }
  // "untouched" -> no report_dest key, ever, in either mode.

  if (state.client && state.choice.kind !== "internal_only") {
    result.client_intent = true;
  }

  return result;
}

/**
 * The inverse, for seeding the edit screen from the entry being opened.
 *
 * Without this the destination boxes render EMPTY on an entry that is already on
 * the daily report, so the screen shows the opposite of the truth and there is
 * nothing to un-tick. Lives here because the wire shape must be read in the same
 * place it is written, or the two drift.
 *
 * An unrecognised value degrades to `untouched` rather than throwing: a save
 * screen that will not open is worse than one whose boxes start blank.
 */
export function deserializeDest(
  payload: Record<string, unknown> | undefined,
  options: { legacyEffective?: boolean } = {},
): DestUIState {
  const client = payload?.client_intent === true;
  const raw = payload?.report_dest;
  const parsed = parseReportDest(raw);

  if (parsed === "internal_only") return { choice: { kind: "internal_only" }, client: false };
  if (Array.isArray(parsed)) {
    return {
      choice: {
        kind: "reports",
        daily: parsed.includes("daily"),
        weekly: parsed.includes("weekly"),
      },
      client,
    };
  }
  if ((raw === undefined || raw === null) && options.legacyEffective === true) {
    // Skill 6's existing legacy rule admits an unpinned row to BOTH report
    // windows. Blank boxes would state the opposite and leave nothing to clear.
    return { choice: { kind: "reports", daily: true, weekly: true }, client };
  }
  // Absent/null/unknown on a fresh form: no human choice has been made.
  return { choice: { kind: "untouched" }, client };
}

/** Atomic parser for the closed report destination union. Unknown members,
 * duplicates and non-canonical ordering invalidate the entire value. */
export function parseReportDest(raw: unknown): "internal_only" | Array<"daily" | "weekly"> | undefined {
  if (raw === "internal_only") return raw;
  if (!Array.isArray(raw)) return undefined;
  if (raw.length === 0) return [];
  if (raw.length === 1 && (raw[0] === "daily" || raw[0] === "weekly")) return [raw[0]];
  if (raw.length === 2 && raw[0] === "daily" && raw[1] === "weekly") return ["daily", "weekly"];
  return undefined;
}

/** Fresh captures must not inherit Skill 6's legacy BOTH-reports default. */
export function explicitNewCaptureDest(dest: SerializedDest): SerializedDest {
  return "report_dest" in dest ? dest : { ...dest, report_dest: [] };
}
